import { mock$beInitReaderPayment } from 'actions/mock/castor.mock';
import { CAP_POS_INIT_READER, CAP_POS_INVALID_READER, checkCapFlag } from 'bl/captures.bl';
import { LT_POS } from 'bl/loggers.bl';
import SENTRY from 'config/sentry.singleton';
import H from 'constants/httpMethod.const';
import SF from 'constants/salonFlag.const';
import S from 'constants/string.const';
import fromInitReaderPaymentToDto from 'dto/fromInitReaderPaymentToDto.dto';
import asArray from 'utils/arrays/asArray.util';
import endpoint from 'utils/backend/endpoint.util';
import mockJsonResponse from 'utils/backend/mockJsonResponse.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromObjectToEntries from 'utils/converters/object/fromObjectToEntries.util';
import logger from 'utils/logger.util';
import actionBe from 'utils/mw/actionBe.util';
import olen from 'utils/objects/olen.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import { isNotValidId } from 'utils/predicates/fields.util';
import isPrice from 'utils/predicates/isPrice.util';
import isFullString from 'utils/predicates/string/isFullString.util';

const CTAG = 'beInitReaderPayment';
const L = logger([CTAG, LT_POS]);

const USE_VALIDATE_RETURN = false;

const fromErrorsToString = (errors) => {
  try {
    if (null === errors || void 1 === errors) return '';
    if (Array.isArray(errors)) return (errors.length ? ': ' : '') + errors.join(' ;; ');
    if (!olen(errors)) return '';

    const entries = fromObjectToEntries(errors);
    if (!entries.length) return '';

    return `: ${entries.map(([k, v]) => `${fromAnyToTrimmed(k)}: ${fromAnyToTrimmed(v)}`).join(' ;; ')}`;
  } catch (e) {
    L.warn('fromErrorsToString()', e);
  }
  return '';
};

// eslint-disable-next-line no-unused-vars
const MOCK = Object.freeze({
  notReady: () =>
    mockJsonResponse({
      status: 400,
      body: { error: 'your reader is not ready' },
    }),
  unreachable: () =>
    mockJsonResponse({
      status: 400,
      body: {
        error: [
          'Reader is currently unreachable,',
          'please ensure the reader is powered on and connected to the internet',
          'before retrying your request.',
        ].join(S.space),
      },
    }),
});

const validateBeInitReaderPayment = (o) => {
  const e = [];

  if (!isFullString(o.readerId)) e.push(`Bad POS reader ID: ${o.readerId}`);
  if (isNotValidId(o.clientId)) e.push(`Bad client ID: ${o.clientId}`);
  if (isNotValidId(o.appointmentId)) e.push(`Bad appointment ID: ${o.appointmentId}`);
  if (!isPrice(o.amount)) e.push(`Not a valid amount: ${o.amount}`);

  if (!isFullArray(o.items)) e.push(`No items`);

  asArray(o.items).forEach((item, index) => {
    const n = index + 1;
    if (!isFullString(item.description)) e.push(`No description for item ${n}`);
    if (!isPrice(item.price)) e.push(`No valid price for item ${n}`);
    if (!Number.isSafeInteger(item.quantity)) e.push(`No valid quantity for item ${n}`);
  });

  return e.length ? e : void 1;
};

// SEE: https://github.com/getslick/api.core/blob/staging/docs/pos.md

const beInitReaderPayment = actionBe('beInitReaderPayment', (options) => {
  const { readerId, AT, ATAG, flags } = options;

  const data = fromInitReaderPaymentToDto(options);
  const errors = validateBeInitReaderPayment(options);

  if (errors) {
    L.warn('()', 'Validation errors:', errors);

    if (checkCapFlag({ flags, prefix: SF.beInitReaderPayment }).info)
      SENTRY.info({
        logger: CTAG === ATAG ? CTAG : [CTAG, ATAG],
        tags: { ATAG, readerId },
        extra: { ATAG, readerId, errors, options, data },
        message: CAP_POS_INIT_READER + fromErrorsToString(errors),
      });

    if (USE_VALIDATE_RETURN) return { type: AT.fail, errors, errorMessage: errors.join('. ') };
  }

  if (!readerId) {
    L.warn('()', 'Invalid readerId:', readerId);

    if (checkCapFlag({ flags, prefix: SF.beInitReaderPayment }).info)
      SENTRY.info({
        logger: CTAG === ATAG ? CTAG : [CTAG, ATAG],
        tags: { ATAG, readerId },
        extra: { ATAG, readerId, errors, options, data },
        message: CAP_POS_INVALID_READER,
      });
  }

  return endpoint(H.post, 'payments/stripe/process/payment/:salonId/:readerId/', {
    AT,
    name: ATAG,
    params: { readerId },
    data,
    mock: mock$beInitReaderPayment,
  });
});

// noinspection JSUnusedGlobalSymbols
export default beInitReaderPayment;
